import React, { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  TextField,
  PrimaryButton,
  Checkbox,
  MessageBar,
  MessageBarType,
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  ActionButton,
  Spinner,
  SpinnerSize,
  Text,
  Link
} from '@fluentui/react';
import type { IColumn } from '@fluentui/react';
import * as smsApi from '../api';
import type { SmsEnrollment } from '../types';

interface SmsEnrollmentCardProps {
  onNotification?: (type: MessageBarType, message: string) => void;
}

/**
 * SMS Enrollment Card Component
 * Allows users to add and manage phone numbers for SMS notifications
 */
export const SmsEnrollmentCard: React.FC<SmsEnrollmentCardProps> = ({ onNotification }) => {
  const [enrollments, setEnrollments] = useState<SmsEnrollment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState(30);
  const [phoneInput, setPhoneInput] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [termsPrivacyChecked, setTermsPrivacyChecked] = useState(false);
  const [fromNumber, setFromNumber] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const joinTargetText = fromNumber || 'the designated company SMS number';
  const hasPendingEnrollments = enrollments.some((e) => !e.consentReceived);

  const formatPhoneNumberForDisplay = (e164PhoneNumber: string): string => {
    if (!e164PhoneNumber) {
      return '';
    }

    const digitsOnly = e164PhoneNumber.replace(/\D/g, '');

    if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return `(${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
    }

    if (digitsOnly.length === 10) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    }

    return e164PhoneNumber;
  };

  const loadData = useCallback(async (showInitialLoader: boolean) => {
    try {
      if (showInitialLoader) {
        setIsLoading(true);
      }

      const [enrollmentsData, fromNumberData] = await Promise.all([
        smsApi.getSmsEnrollments(),
        smsApi.getSmsFromNumber()
      ]);

      setEnrollments(enrollmentsData);
      setFromNumber(fromNumberData);
      setErrorMessage('');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load SMS enrollments';
      setErrorMessage(message);
      if (onNotification) {
        onNotification(MessageBarType.error, message);
      }
    } finally {
      if (showInitialLoader) {
        setIsLoading(false);
      }
    }
  }, [onNotification]);

  // Load initial data
  useEffect(() => {
    void loadData(true);
  }, [loadData]);

  // Keep enrollment statuses in sync with Twilio webhook updates.
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      setSecondsUntilRefresh((previousSeconds) => {
        if (previousSeconds <= 1) {
          void loadData(false);
          return 30;
        }

        return previousSeconds - 1;
      });
    }, 1000);

    return () => clearInterval(refreshInterval);
  }, [loadData]);

  // Format phone number input as user types
  const handlePhoneInputChange = (_event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    if (!newValue) {
      setPhoneInput('');
      return;
    }

    // Extract only digits
    const digitsOnly = newValue.replace(/\D/g, '');

    // Format as (XXX) XXX-XXXX
    let formatted = '';
    if (digitsOnly.length > 0) {
      if (digitsOnly.length <= 3) {
        formatted = `(${digitsOnly}`;
      } else if (digitsOnly.length <= 6) {
        formatted = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
      } else {
        formatted = `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
      }
    }

    setPhoneInput(formatted);
  };

  // Enroll a new phone number
  const handleEnroll = async () => {
    if (!phoneInput.trim()) {
      setErrorMessage('Please enter a phone number');
      return;
    }

    if (!consentChecked) {
      setErrorMessage('Please acknowledge the SMS consent disclosure before adding your number');
      return;
    }

    if (!termsPrivacyChecked) {
      setErrorMessage('Please certify that you agree to the terms and conditions and privacy policy before adding your number');
      return;
    }

    try {
      setIsSaveLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      const response = await smsApi.enrollPhoneNumber(phoneInput);
      const configuredFromNumber = response.fromPhoneNumber || fromNumber;
      if (response.fromPhoneNumber && response.fromPhoneNumber !== fromNumber) {
        setFromNumber(response.fromPhoneNumber);
      }

      // Add or update the enrollment in the list
      setEnrollments((prev) => {
        const index = prev.findIndex((e) => e.id === response.enrollment.id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = response.enrollment;
          return updated;
        }
        return [...prev, response.enrollment];
      });

      setSuccessMessage(
        configuredFromNumber
          ? `Phone number enrolled! Text JOIN to ${configuredFromNumber} to activate SMS notifications.`
          : 'Phone number enrolled! Text JOIN to the designated company SMS number to activate SMS notifications.'
      );
      setPhoneInput('');
      setConsentChecked(false);
      setTermsPrivacyChecked(false);

      if (onNotification) {
        onNotification(MessageBarType.success, response.message);
      }
    } catch (error) {
      const message =
        error instanceof Error && error.message.includes('409')
          ? 'This phone number is already enrolled or registered to another user'
          : error instanceof Error
            ? error.message
            : 'Failed to enroll phone number';
      setErrorMessage(message);
      if (onNotification) {
        onNotification(MessageBarType.error, message);
      }
    } finally {
      setIsSaveLoading(false);
    }
  };

  // Remove a phone number enrollment
  const handleRemove = async (enrollmentId: number) => {
    try {
      setIsSaveLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      await smsApi.removePhoneNumber(enrollmentId);

      setEnrollments((prev) => prev.filter((e) => e.id !== enrollmentId));

      const message = 'Phone number removed successfully';
      setSuccessMessage(message);
      if (onNotification) {
        onNotification(MessageBarType.success, message);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove phone number';
      setErrorMessage(message);
      if (onNotification) {
        onNotification(MessageBarType.error, message);
      }
    } finally {
      setIsSaveLoading(false);
    }
  };

  // Send a test SMS
  const handleTestSms = async (enrollmentId: number) => {
    try {
      setErrorMessage('');
      setSuccessMessage('');

      await smsApi.sendTestSms(enrollmentId);

      const message = 'Test SMS sent!';
      setSuccessMessage(message);
      if (onNotification) {
        onNotification(MessageBarType.success, message);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send test SMS';
      setErrorMessage(message);
      if (onNotification) {
        onNotification(MessageBarType.error, message);
      }
    }
  };

  // Define columns for the enrollments table
  const columns: IColumn[] = [
    {
      key: 'phoneNumber',
      name: 'Phone Number',
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
      onRender: (item: SmsEnrollment) => formatPhoneNumberForDisplay(item.phoneNumber)
    },
    {
      key: 'status',
      name: 'Status',
      minWidth: 100,
      maxWidth: 150,
      onRender: (item: SmsEnrollment) => (
        <Text variant="small">
          {item.consentReceived ? (
            <span style={{ color: 'green' }}>Active</span>
          ) : (
            <span style={{ color: 'orange' }}>Pending</span>
          )}
        </Text>
      )
    },
    {
      key: 'actions',
      name: 'Actions',
      minWidth: 150,
      maxWidth: 200,
      onRender: (item: SmsEnrollment) => (
        <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
          <ActionButton
            iconProps={{ iconName: 'Send' }}
            onClick={() => handleTestSms(item.id)}
            disabled={!item.consentReceived || isSaveLoading}
            title="Send a test SMS"
          >
            Test
          </ActionButton>
          <ActionButton
            iconProps={{ iconName: 'Delete' }}
            onClick={() => handleRemove(item.id)}
            disabled={isSaveLoading}
            title="Remove this phone number"
          >
            Remove
          </ActionButton>
        </Stack>
      )
    }
  ];

  if (isLoading) {
    return (
      <Stack tokens={{ padding: '20px' }} horizontalAlign="center">
        <Spinner size={SpinnerSize.medium} label="Loading SMS enrollments..." />
      </Stack>
    );
  }

  return (
    <Stack tokens={{ padding: '20px', childrenGap: '16px' }}>
      <Stack>
        <Text variant="xLarge" block styles={{ root: { marginBottom: '8px' } }}>
          SMS Enrollment
        </Text>
        <Text variant="small" block styles={{ root: { color: '#666' } }}>
          Enroll phone numbers to receive on-call SMS notifications.
        </Text>
      </Stack>

      {/* CTA Disclosure */}
      <Stack
        tokens={{ childrenGap: '10px', padding: '10px 14px 12px' }}
        styles={{
          root: {
            backgroundColor: '#0f172a',
            color: '#f8fafc',
            border: '1px solid #38bdf8',
            borderLeft: '4px solid #38bdf8',
            borderRadius: '6px'
          }
        }}
      >
        <Text variant="medium" block styles={{ root: { color: '#f8fafc', fontWeight: 600, lineHeight: '22px' } }}>
          SMS Consent Disclosure (Required)
        </Text>
        <Text variant="small" block styles={{ root: { color: '#f8fafc', lineHeight: '20px', fontWeight: 400 } }}>
          By enrolling your phone number, you consent to receive recurring automated SMS notifications from{' '}
          Networking Technologies On-Call Manager related to your employment (on-call alerts,
          outages, escalations, and reminders).
        </Text>
        <Text variant="small" block styles={{ root: { color: '#f8fafc', lineHeight: '20px', fontWeight: 400 } }}>
          <strong>Message frequency will vary depending on the number of on-call tickets received, and the response time of the on-call technician. Messages will only be received when the technician is on-call.</strong>{' '}
          <strong>Message and data rates may apply.</strong>
        </Text>
        <Text variant="small" block styles={{ root: { color: '#f8fafc', lineHeight: '20px', fontWeight: 400 } }}>
          Reply <strong>STOP</strong> to opt out at any time. Reply <strong>HELP</strong> for help, or contact{' '}
          <Link href="mailto:service@net-cloud.com" styles={{ root: { color: '#93c5fd', textDecoration: 'underline' } }}>
            service@net-cloud.com
          </Link>{' '}
          / 814-836-0000.
        </Text>
        <Text variant="small" block styles={{ root: { color: '#f8fafc', lineHeight: '20px', fontWeight: 400 } }}>
          See our{' '}
          <Link
            href="https://www.net-cloud.com/employee-short-message-service-sms-terms-conditions/"
            target="_blank"
            styles={{ root: { color: '#93c5fd', textDecoration: 'underline' } }}
          >
            Terms &amp; Conditions
          </Link>{' '}
          and{' '}
          <Link
            href="https://www.net-cloud.com/employee-short-message-service-sms-privacy-policy/"
            target="_blank"
            styles={{ root: { color: '#93c5fd', textDecoration: 'underline' } }}
          >
            Privacy Policy
          </Link>
          .
        </Text>
        <details style={{ color: '#f8fafc' }}>
          <summary style={{ cursor: 'pointer', color: '#93c5fd' }}>Show all supported SMS commands</summary>
          <Text variant="small" block styles={{ root: { color: '#f8fafc', lineHeight: '20px', marginTop: '6px', fontWeight: 400 } }}>
            Opt in/re-enable: JOIN, START, YES, UNSTOP. Help: HELP. Opt out: STOP, STOPALL, UNSUBSCRIBE, CANCEL,
            END, QUIT, OPTOUT, REVOKE.
          </Text>
        </details>
      </Stack>

      {errorMessage && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline onDismiss={() => setErrorMessage('')}>
          {errorMessage}
        </MessageBar>
      )}

      {successMessage && (
        <MessageBar messageBarType={MessageBarType.success} isMultiline onDismiss={() => setSuccessMessage('')}>
          {successMessage}
        </MessageBar>
      )}

      {/* Enrollment Form */}
      <Stack tokens={{ childrenGap: '12px' }}>
        <Checkbox
          label="By checking this checkbox agree to receive SMS communications related to on-call technician notifications during your scheduled on-call period."
          checked={consentChecked}
          onChange={(_event, checked) => setConsentChecked(!!checked)}
          disabled={isSaveLoading}
          required
          styles={{ root: { marginBottom: '4px' } }}
        />
        <Checkbox
          label="By checking this box I certify that I have agree to the terms and conditions and privay policy."
          checked={termsPrivacyChecked}
          onChange={(_event, checked) => setTermsPrivacyChecked(!!checked)}
          disabled={isSaveLoading}
          required
          styles={{ root: { marginBottom: '4px' } }}
        />
        <Stack horizontal tokens={{ childrenGap: '12px' }} verticalAlign="end">
          <TextField
            label="Phone Number"
            placeholder="(555) 123-4567"
            value={phoneInput}
            onChange={handlePhoneInputChange}
            disabled={isSaveLoading}
            description="Enter your 10-digit phone number"
            styles={{ root: { minWidth: '300px' } }}
          />
          <PrimaryButton
            text="Add Number"
            onClick={handleEnroll}
            disabled={isSaveLoading || !phoneInput.trim() || !consentChecked || !termsPrivacyChecked}
            styles={{ root: { marginBottom: '0' } }}
          />
        </Stack>
      </Stack>

      {/* Enrollments List */}
      <Stack tokens={{ childrenGap: '12px' }}>
        <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
          <Text variant="large" block>
            Your Enrollments ({enrollments.length})
          </Text>
          <Text variant="small" styles={{ root: { color: '#666' } }}>
            Refreshing in {secondsUntilRefresh}s
          </Text>
        </Stack>

        {enrollments.length === 0 ? (
          <Text variant="medium" styles={{ root: { fontStyle: 'italic', color: '#999' } }}>
            No phone numbers enrolled yet. Add one above to get started!
          </Text>
        ) : (
          <DetailsList
            items={enrollments}
            columns={columns}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
            styles={{
              root: {
                border: '1px solid #ddd',
                borderRadius: '4px'
              }
            }}
          />
        )}
      </Stack>

      {/* Info message about JOIN */}
      {hasPendingEnrollments && (
        <MessageBar messageBarType={MessageBarType.info}>
          <strong>Pending Confirmation:</strong> Text JOIN to {joinTargetText} from your phone number to activate SMS
          notifications. Pending numbers show status as "Pending" above.
        </MessageBar>
      )}
    </Stack>
  );
};

